import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
const initialState = {
    accounts: [],
    accountsLoading: false,
    accountsError: null,
    rows: [],
    rowsLoading: false,
    rowsError: null,
    page: 1,
    totalPages: 1,
    total: 0,
    previewOptions: [],
    lastSyncedAt: null,
    syncing: false,
    lastListParams: null,
    takeActionCloseNonce: 0,
};
/* ========== Thunks ========== */
export const fetchAdApprovalAccounts = createAsyncThunk("clientAdApproval/fetchAccounts", async ({ clientId }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/client-store/connections/${clientId}/meta`, "", token ? { Authorization: `Bearer ${token}` } : undefined);
        let metaConnections = [];
        if (res && "data" in res && res.status === 200 && res.data?.success) {
            metaConnections = Array.isArray(res.data.data) ? res.data.data : [];
        }
        const options = metaConnections.map((c) => {
            const id = String(c?._id || c?.id || "");
            const accountName = String(c?.account_name || "").trim();
            const accountValue = c?.value != null ? String(c.value) : "";
            return {
                value: id,
                accountId: accountValue || undefined,
                label: (accountName && accountValue
                    ? `${accountName} (${accountValue})`
                    : accountName || accountValue || id).toUpperCase(),
            };
        }).filter((o) => Boolean(o.value));
        return { options };
    }
    catch (err) {
        return rejectWithValue(err?.message || "Error fetching accounts");
    }
});
export const fetchClientAdApprovals = createAsyncThunk("clientAdApproval/fetchList", async ({ params }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/ad-approval/list?${params.toString()}`, "", token ? { Authorization: `Bearer ${token}` } : undefined);
        if (res && "data" in res && res.status === 200 && res.data?.success) {
            const data = res.data.data;
            const rows = Array.isArray(data?.ads) ? data.ads : [];
            const previewOptions = Array.isArray(data?.previewOptions) ? data.previewOptions : [];
            const lastSyncedAt = data?.lastSyncedAt != null ? String(data.lastSyncedAt) : null;
            return {
                rows,
                total: data?.total ?? rows.length,
                totalPages: data?.totalPages ?? 1,
                page: data?.page ?? 1,
                previewOptions,
                lastSyncedAt,
            };
        }
        return rejectWithValue(res?.data?.message || "Failed to fetch client ad approvals");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Error fetching client ad approvals");
    }
});
export const refetchClientAdApprovals = createAsyncThunk("clientAdApproval/refetchList", async (_, { getState, dispatch, rejectWithValue }) => {
    try {
        const raw = getState().clientAdApproval.lastListParams;
        if (!raw || !String(raw).trim()) {
            return;
        }
        const params = new URLSearchParams(String(raw));
        await dispatch(fetchClientAdApprovals({ params }));
        return;
    }
    catch (err) {
        return rejectWithValue(err?.message || "Error refetching client ad approvals");
    }
});
export const syncClientAdApprovals = createAsyncThunk("clientAdApproval/sync", async ({ clientId, connectionId }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const params = new URLSearchParams();
        params.append("clientId", clientId);
        if (connectionId) {
            params.append("connectionId", connectionId);
        }
        const res = await ApiCall("GET", `/api/ad-approval/sync?${params.toString()}`, "", token ? { Authorization: `Bearer ${token}` } : undefined);
        if (res && "data" in res && res.data?.success) {
            toast.success("Ad approvals refreshed successfully.");
            return;
        }
        const msg = res?.data?.message || "Failed to sync client ad approvals.";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Error syncing client ad approvals.";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const deleteManualAdRequest = createAsyncThunk("clientAdApproval/deleteManualRequest", async ({ id }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/ad-approval/requests/${id}`, "", token ? { Authorization: `Bearer ${token}` } : undefined);
        if (!res || "isAxiosError" in res) {
            throw new Error("Failed to delete ad request.");
        }
        const payload = res.data;
        if (!payload?.success) {
            throw new Error(payload?.message || "Failed to delete ad request.");
        }
        return id;
    }
    catch (err) {
        const msg = err?.message || "Error deleting ad request.";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const createManualAdRequest = createAsyncThunk("clientAdApproval/createManualRequest", async ({ formData }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/ad-approval/requests`, formData, token ? { Authorization: `Bearer ${token}` } : undefined);
        if (!res || "isAxiosError" in res) {
            throw new Error("Failed to create ad request.");
        }
        const payload = res.data;
        if (!payload?.success) {
            throw new Error(payload?.message || "Failed to create ad request.");
        }
        toast.success("Ad request created successfully.");
        return;
    }
    catch (err) {
        const msg = err?.message || "Error creating ad request.";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const updateManualAdRequest = createAsyncThunk("clientAdApproval/updateManualRequest", async ({ id, formData }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("PATCH", `/api/ad-approval/requests/${id}`, formData, token ? { Authorization: `Bearer ${token}` } : undefined);
        if (!res || "isAxiosError" in res) {
            throw new Error("Failed to update ad request.");
        }
        const payload = res.data;
        if (!payload?.success) {
            throw new Error(payload?.message || "Failed to update ad request.");
        }
        toast.success("Ad request updated successfully.");
        return;
    }
    catch (err) {
        const msg = err?.message || "Error updating ad request.";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const reorderClientAdApprovals = createAsyncThunk("clientAdApproval/reorder", async ({ connectionId, tab, orderedIds }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("PATCH", `/api/ad-approval/reorder`, {
            connectionId,
            tab,
            orderedIds: Array.isArray(orderedIds) ? orderedIds : [],
        }, token ? { Authorization: `Bearer ${token}` } : undefined);
        if (res && "data" in res && res.data?.success) {
            return;
        }
        return rejectWithValue(res?.data?.message || "Failed to reorder client ad approvals");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Error reordering client ad approvals");
    }
});
export const fetchAdPreview = createAsyncThunk("clientAdApproval/fetchPreview", async (args, { getState, rejectWithValue }) => {
    const { adId, format, accountId, toastOnError = true } = args;
    try {
        const token = getState().auth?.token;
        const params = new URLSearchParams();
        params.append("adsetId", adId);
        params.append("adFormat", format);
        if (accountId)
            params.append("accountId", accountId);
        const res = await ApiCall("GET", `/api/ad-approval/preview?${params.toString()}`, "", token ? { Authorization: `Bearer ${token}` } : undefined);
        if (!res || "isAxiosError" in res) {
            throw new Error("Failed to fetch ad preview.");
        }
        const payload = res.data;
        if (!payload?.success) {
            throw new Error(payload?.message || "Failed to fetch ad preview.");
        }
        const data = payload.data;
        const html = typeof data?.html === "string" && data.html.trim().length > 0
            ? data.html
            : null;
        const previewUrl = typeof data?.preview_shareable_link === "string" &&
            data.preview_shareable_link.trim().length > 0
            ? data.preview_shareable_link
            : null;
        return { html, previewUrl };
    }
    catch (err) {
        const message = err?.message || "Error fetching ad preview.";
        if (toastOnError)
            toast.error(message);
        return rejectWithValue(message);
    }
});
/* NOTE: Preview is still called
   directly from the page, since it needs local UI state (drawer, format selector, etc.).
   It already passes the token correctly via ApiCall and does not need slice state. */
/* ========== Slice ========== */
const clientAdApprovalSlice = createSlice({
    name: "clientAdApproval",
    initialState,
    reducers: {
        setPage(state, action) {
            state.page = action.payload;
        },
        requestCloseTakeAction(state) {
            state.takeActionCloseNonce += 1;
        },
        clearList(state) {
            state.rows = [];
            state.total = 0;
            state.totalPages = 1;
            state.page = 1;
        },
    },
    extraReducers: (builder) => builder
        // Accounts
        .addCase(fetchAdApprovalAccounts.pending, (state) => {
        state.accountsLoading = true;
        state.accountsError = null;
    })
        .addCase(fetchAdApprovalAccounts.fulfilled, (state, action) => {
        state.accountsLoading = false;
        state.accounts = action.payload.options;
    })
        .addCase(fetchAdApprovalAccounts.rejected, (state, action) => {
        state.accountsLoading = false;
        state.accountsError = action.payload || null;
        state.accounts = [];
    })
        // List
        .addCase(fetchClientAdApprovals.pending, (state, action) => {
        state.rowsLoading = true;
        state.rowsError = null;
        state.lastListParams = action?.meta?.arg?.params?.toString?.() ?? null;
    })
        .addCase(fetchClientAdApprovals.fulfilled, (state, action) => {
        state.rowsLoading = false;
        state.rows = action.payload.rows;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.page = action.payload.page;
        state.previewOptions = Array.isArray(action.payload.previewOptions)
            ? action.payload.previewOptions
            : [];
        state.lastSyncedAt =
            action.payload.lastSyncedAt != null ? action.payload.lastSyncedAt : null;
    })
        .addCase(fetchClientAdApprovals.rejected, (state, action) => {
        state.rowsLoading = false;
        state.rowsError = action.payload || null;
        state.rows = [];
    })
        // Sync
        .addCase(syncClientAdApprovals.pending, (state) => {
        state.syncing = true;
    })
        .addCase(syncClientAdApprovals.fulfilled, (state) => {
        state.syncing = false;
    })
        .addCase(syncClientAdApprovals.rejected, (state) => {
        state.syncing = false;
    })
        .addCase(deleteManualAdRequest.fulfilled, (state, action) => {
        const id = action.payload;
        state.rows = state.rows.filter((r) => r._id !== id);
        state.total = Math.max(0, state.total - 1);
    }),
});
export const { setPage, requestCloseTakeAction, clearList } = clientAdApprovalSlice.actions;
// Selectors
export const selectClientAdApprovalAccounts = (state) => state.clientAdApproval.accounts;
export const selectClientAdApprovalAccountsLoading = (state) => state.clientAdApproval.accountsLoading;
export const selectClientAdApprovalRows = (state) => state.clientAdApproval.rows;
export const selectClientAdApprovalRowsLoading = (state) => state.clientAdApproval.rowsLoading;
export const selectClientAdApprovalPagination = createSelector((state) => state.clientAdApproval.page, (state) => state.clientAdApproval.totalPages, (state) => state.clientAdApproval.total, (page, totalPages, total) => ({ page, totalPages, total }));
export const selectClientAdApprovalSyncing = (state) => state.clientAdApproval.syncing;
export const selectTakeActionCloseNonce = (state) => state.clientAdApproval.takeActionCloseNonce;
export const selectClientAdApprovalPreviewOptions = (state) => state.clientAdApproval.previewOptions;
export const selectClientAdApprovalLastSyncedAt = (state) => state.clientAdApproval.lastSyncedAt;
export default clientAdApprovalSlice.reducer;
